import { debounce } from "lodash";
import React, {
  createContext,
  RefObject,
  useContext,
  useMemo,
  useRef,
} from "react";

interface ScrollContextType {
  monitorTranscriptAutoScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  setTextContainerRef: (ref: RefObject<HTMLDivElement>) => void;
  setBottomContainerRef: (ref: RefObject<HTMLDivElement>) => void;
  isAutoScroll: boolean;
  setIsAutoScroll: (isAutoScroll: boolean) => void;
  setScrollFreezeUntil: (until: number) => void;
}

const ScrollContext = createContext<ScrollContextType | undefined>(undefined);

export const ScrollProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [textContainerRef, setTextContainerRef] =
    React.useState<RefObject<HTMLDivElement> | null>(null);
  const [bottomContainerRef, setBottomContainerRef] =
    React.useState<RefObject<HTMLDivElement> | null>(null);
  const [isAutoScroll, setIsAutoScroll] = React.useState(false);
  const scrollFreezeUntil = useRef(0);
  const setScrollFreezeUntil = (until: number) => {
    scrollFreezeUntil.current = Date.now() + until;
  };

  const handleAutoScroll = () => {
    const debouncedScroll = debounce(() => {
      if (Date.now() < scrollFreezeUntil.current) return;

      const container = textContainerRef?.current;
      const bottom = bottomContainerRef?.current;
      if (!container || !bottom) return;

      const containerRect = container.getBoundingClientRect();
      const bottomRect = bottom.getBoundingClientRect();

      const distanceFromBottom = bottomRect.bottom - containerRect.bottom;

      const isAtBottom = Math.abs(distanceFromBottom) <= 200;
      if (!isAtBottom) {
        setIsAutoScroll(false);
      }
    }, 100);

    debouncedScroll();
  };

  const monitorTranscriptAutoScroll = (
    event: React.UIEvent<HTMLDivElement>,
  ) => {
    handleAutoScroll();
  };

  const contextValue = useMemo(
    () => ({
      monitorTranscriptAutoScroll,
      setTextContainerRef,
      setBottomContainerRef,
      isAutoScroll,
      setIsAutoScroll,
      setScrollFreezeUntil,
    }),
    [isAutoScroll],
  );

  return (
    <ScrollContext.Provider value={contextValue}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (context === undefined) {
    throw new Error("useScroll must be used within a ScrollProvider");
  }
  return context;
};
